import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '../../services/error-handler.service';

@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    MatAngularModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  standalone: true,
})
export class ResetPasswordComponent implements OnInit {
  fb = inject(FormBuilder);
  route = inject(ActivatedRoute);
  router = inject(Router);
  authService = inject(AuthService);
  snackBar = inject(MatSnackBar);
  errorHandler = inject(ErrorHandlerService);

  form: FormGroup = this.fb.group({
    new_password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
    confirm_password: ['', Validators.required],
  }, { validators: this.passwordMatchValidator });

  token = '';
  isLoading = false;
  success = false;
  invalidToken = false;

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.invalidToken = true;
    }
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const v = control.value;
    if (!v) return null;
    const errors: ValidationErrors = {};
    if (v.length < 8) errors['minlength'] = true;
    if (!/[A-Z]/.test(v)) errors['noUppercase'] = true;
    if (!/[a-z]/.test(v)) errors['noLowercase'] = true;
    if (!/[0-9]/.test(v)) errors['noDigit'] = true;
    return Object.keys(errors).length > 0 ? errors : null;
  }

  passwordMatchValidator(g: FormGroup): ValidationErrors | null {
    const p = g.get('new_password')?.value;
    const c = g.get('confirm_password')?.value;
    return p && c && p !== c ? { mismatch: true } : null;
  }

  async submit() {
    if (this.form.invalid || !this.token) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    try {
      await this.authService.resetPassword(this.token, this.form.value.new_password).toPromise();
      this.success = true;
      this.snackBar.open('Password updated successfully. You can now log in.', 'Close', {
        duration: 4000,
        panelClass: ['success-snackbar'],
      });
      setTimeout(() => this.router.navigate(['/login']), 2000);
    } catch (error: unknown) {
      const msg = this.errorHandler.extractErrorMessage(error, 'Failed to reset password.');
      this.snackBar.open(msg.includes('invalid') || msg.includes('expired') ? 'Link invalid or expired. Please request a new password reset.' : msg, 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar'],
      });
      this.invalidToken = true;
    } finally {
      this.isLoading = false;
    }
  }

  getErrorMessage(controlName: string): string {
    const c = this.form.get(controlName);
    if (c?.hasError('required')) return controlName === 'confirm_password' ? 'Please confirm your password' : 'Password is required';
    if (c?.hasError('minlength')) return 'Password must be at least 8 characters';
    if (c?.hasError('noUppercase')) return 'Password must contain an uppercase letter';
    if (c?.hasError('noLowercase')) return 'Password must contain a lowercase letter';
    if (c?.hasError('noDigit')) return 'Password must contain a digit';
    if (this.form.hasError('mismatch') && controlName === 'confirm_password') return 'Passwords do not match';
    return '';
  }
}
